import React from 'react';
import { Link } from 'react-router-dom';
import VisualizerBackground from '../components/VisualizerBackground';
import Visualizer from '../components/Visualizer';
import ScrollAnimation from '../components/ScrollAnimation';
import ParallaxSection from '../components/ParallaxSection';
import OptimizedImage from '../components/OptimizedImage';
import { venuePhotos } from '../data/photos';

const About = () => {
  return (
    <div className="min-h-screen bg-drummer-dark text-white">
      <VisualizerBackground />
      
      {/* Hero Section */}
      <section className="pt-28 pb-8 md:pt-36 md:pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollAnimation animation="fadeUp">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">The Artistry of</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">
                  Daryian Kelton
                </span>
              </h1>
              <p className="text-xl text-white">
                Precision, creativity, and technical excellence across multiple artistic domains.
              </p>
            </ScrollAnimation>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-8 md:py-16 relative">
        <ParallaxSection className="absolute top-40 right-10 w-72 h-72 bg-drummer-purple/5 rounded-full blur-3xl -z-10" speed={-0.4}>
          <div className="w-full h-full" />
        </ParallaxSection>
        <div className="container mx-auto px-4">
          {/* The Rhythm of Life */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-32">
            <ScrollAnimation animation="slideRight" delay={0.1} duration={0.6}>
              <div className="electric-border">
                <div className="aspect-w-4 aspect-h-5 rounded-lg overflow-hidden">
                  <OptimizedImage 
                    src={venuePhotos.rockwood.main}
                    alt={venuePhotos.rockwood.alt}
                    className="w-full h-full object-cover"
                    width={800}
                    height={1000}
                    priority={true}
                  />
                </div>
              </div>
            </ScrollAnimation>
            <ScrollAnimation animation="slideLeft" delay={0.3} duration={0.6}>
              <div>
                <h2 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Professional Excellence</h2>
                <div className="space-y-4 text-white">
                  <p>
                    Daryian Kelton's career exemplifies the highest standards of technical precision and creative innovation. 
                    With years of dedicated practice and professional experience, he has established himself as a standout performer and creator.
                  </p>
                  <p>
                    As a drummer, Daryian combines meticulous technique with expressive musicality, 
                    enabling him to excel across diverse musical contexts while maintaining his distinctive artistic voice.
                  </p>
                  <p>
                    This commitment to excellence extends to his work in visual media, where his understanding of rhythm, timing, 
                    and composition creates a signature style that transcends conventional boundaries.
                  </p>
                </div>
              </div>
            </ScrollAnimation>
          </div>
          
          {/* Creative Philosophy */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-32">
            <div className="order-2 md:order-1">
              <ScrollAnimation animation="slideRight" delay={0.1} duration={0.7}>
                <h2 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Creative Vision</h2>
                <div className="space-y-4 text-white">
                  <p>
                    "I see creativity as a holistic pursuit where boundaries between disciplines become opportunities for innovation," 
                    says Daryian. "The technical precision of drumming informs my visual compositions, just as visual storytelling 
                    enhances my musical expression."
                  </p>
                  <p>
                    This integrative approach allows Daryian to create work that resonates on multiple levels, 
                    providing clients and audiences with experiences that are both technically impressive and emotionally engaging.
                  </p>
                  <p>
                    Each project—whether musical, visual, or a combination—receives the same level of focused attention, 
                    with a commitment to exceeding expectations through innovative solutions and professional execution.
                  </p>
                </div>
              </ScrollAnimation>
            </div>
            <div className="order-1 md:order-2">
              <ScrollAnimation animation="slideLeft" delay={0.3} duration={0.7}>
                <div className="electric-border">
                  <div className="aspect-w-4 aspect-h-5 rounded-lg overflow-hidden">
                    <OptimizedImage 
                      src={venuePhotos.smiths.main}
                      alt={venuePhotos.smiths.alt}
                      className="w-full h-full object-cover"
                      width={800}
                      height={1000}
                    />
                  </div>
                </div>
              </ScrollAnimation>
            </div>
          </div>
          
          {/* Photo Gallery */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32">
            <ScrollAnimation animation="fadeUp" delay={0.1} duration={0.5}>
              <div className="electric-border">
                <div className="aspect-w-4 aspect-h-3 rounded-lg overflow-hidden">
                  <OptimizedImage 
                    src={venuePhotos.ironcow.first}
                    alt={venuePhotos.ironcow.alt}
                    className="w-full h-full object-cover"
                    width={600}
                    height={450}
                  />
                </div>
              </div>
            </ScrollAnimation>
            <ScrollAnimation animation="fadeUp" delay={0.3} duration={0.5}>
              <div className="electric-border">
                <div className="aspect-w-4 aspect-h-3 rounded-lg overflow-hidden">
                  <OptimizedImage 
                    src={venuePhotos.milestone.first}
                    alt={venuePhotos.milestone.alt}
                    className="w-full h-full object-cover"
                    width={600}
                    height={450}
                  />
                </div>
              </div>
            </ScrollAnimation>
            <ScrollAnimation animation="fadeUp" delay={0.5} duration={0.5}>
              <div className="electric-border">
                <div className="aspect-w-4 aspect-h-3 rounded-lg overflow-hidden">
                  <OptimizedImage 
                    src={venuePhotos.smiths.alt29}
                    alt={venuePhotos.smiths.alt}
                    className="w-full h-full object-cover"
                    width={600}
                    height={450}
                  />
                </div>
              </div>
            </ScrollAnimation>
          </div>
          
          {/* Influences & Inspiration */}
          <ParallaxSection className="absolute bottom-40 left-10 w-80 h-80 bg-client-teal/5 rounded-full blur-3xl -z-10" speed={0.2}>
            <div className="w-full h-full" />
          </ParallaxSection>
          <div className="max-w-3xl mx-auto mb-32">
            <Visualizer className="mb-12" barCount={20} />
            <ScrollAnimation animation="fadeIn" duration={0.8}>
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Professional Journey</h2>
              <div className="space-y-6 text-white">
                <p className="text-lg">
                  Daryian's professional development has been shaped by studying the masters of rhythm and composition across disciplines, 
                  from legendary drummers to pioneering visual artists and filmmakers.
                </p>
                <p className="text-lg">
                  His ongoing commitment to technical refinement and creative growth ensures that his work stays at the cutting edge, 
                  incorporating new technologies and approaches while maintaining the fundamentals of quality and artistic excellence.
                </p>
                <p className="text-lg">
                  As both a collaborator and solo artist, Daryian builds meaningful connections with clients, fellow artists, and audiences, 
                  creating work that resonates on a human level while showcasing professional expertise and technical sophistication.
                </p>
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <ScrollAnimation animation="scaleUp" duration={0.7}>
            <div className="glassmorphic p-10 md:p-16 text-center">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Explore Daryian's World
                </h2>
                <p className="text-xl text-white mb-10">
                  Discover more about Daryian's professional services, watch his performances, or get in touch to start a collaboration.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                  <Link 
                    to="/services" 
                    className="drum-button"
                  >
                    View Services
                  </Link>
                  <Link 
                    to="/videos" 
                    className="drum-button-outline"
                  >
                    Watch Videos
                  </Link>
                  <Link 
                    to="/contact" 
                    className="drum-button-secondary"
                  >
                    Start a Conversation
                  </Link>
                </div>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </section>
    </div>
  );
};

export default About;



